import React, {
  Dispatch, ReactElement, SetStateAction, useEffect, useRef, useState,
} from 'react';
import {
  Image, ScrollView, StyleSheet, Text, View, ActivityIndicator, KeyboardAvoidingView, Platform,
  Pressable,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CameraView } from 'expo-camera';
import Modal from 'react-native-modal';
import * as ImagePicker from 'expo-image-picker';
import { FontAwesome } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { manipulateAsync } from 'expo-image-manipulator';
import Moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import Style from '../../constants/Style';
import CloseModalIcon from '../CloseModalIcon';
import CustomTextField from '../CustomTextField';
import Colors from '../../constants/Colors';
import PlaceholderImageIcon from '../PlaceholderImageIcon';
import BinIcon24 from '../24pxIcons/BinIcon24';
import updateAsset from '../../requests/Assets/UpdateAsset';
import uploadAssetImage from '../../requests/Assets/UploadAssetImage';
import AlertModal from '../AlertModal';
import { AssetFormTab } from './AddAssetModal';
import { Area, Floor, Space } from '../Map/SetFloorsModal/FloorItem';
import DeleteIcon24 from '../24pxIcons/DeleteIcon24';
import isMobile from '../IsMobile';
import {
  ADDITIONAL_FIELD_TYPES, ADDITIONAL_FIELD_TYPE_DETAILS, AdditionalTypeDetails,
} from './AddTemplateModal/components/FieldList/components/FieldItem';
import ResolvedIcon from '../ResolvedIcon';
import ResolvedIconMobile from '../16pxIcons/ResolvedIconMobile';
import AlertIcon12 from '../12pxIcons/AlertIcon12';
import { logEvent } from '../LogEvent';
import retireAsset from '../../requests/Assets/RetireAsset';
import AssetLabelSelector from './AddAssetModal/components/AssetLabelSelector';
import {
  SensorLabel, MeterLabel, TemplateType, AssetInstance, AssetCustomField,
  Ticket,
  UserPermission,
} from '../../constants/Types';
import PressableStyled from '../PressableStyled';
import RegionDropdown from './AddAssetModal/components/RegionDropdown';
import { IMAGE_UPLOAD_FAILURE, UPDATE_ASSET_FAILURE } from '../../constants/AlertMessages';
import BinIcon12 from '../12pxIcons/BinIcon12';
import { updateLocations } from '../../redux/locationStructureSlice';
import GetEntireLocationStructure from '../GetEntireLocationStructure';
import { SelectedTicketState, updateSelectedTicket } from '../../redux/selectedTicketSlice';
import { Building } from '../../screens/LocationManagement/components/AddBuildingModal';
import { getDateComponents } from '../../constants/Utils';

interface EditAssetModalProps {
  customFields: AssetCustomField[],
  asset: AssetInstance,
  image: string,
  visible: boolean,
  setModalVisible: Dispatch<SetStateAction<boolean>>,
  updateImage,
  showAlert?,
  deleteAsset?,
  updateAsset,
  templateType: TemplateType,
}

export default function EditAssetModal(props: EditAssetModalProps) {
  const templateCustomFields: () => AssetCustomField[] = () => JSON.parse(
    JSON.stringify(
      props.customFields
        ?.sort((prev: AssetCustomField, next: AssetCustomField) => prev.name?.toLowerCase()
          .localeCompare(next.name?.toLowerCase()))
        .map((field) => ((field.value?.trim().length > 0
          && field.format === ADDITIONAL_FIELD_TYPES.DATE
          && new Date(field.value) instanceof Date
          && !Number.isNaN(new Date(field.value).getTime()))
          ? { ...field, value: Moment(field.value).format('DD/MM/YYYY') }
          : { ...field })),
    ),
  ) ?? [];

  const [tabSelected, setTabSelected] = useState<AssetFormTab>(AssetFormTab.ESSENTIAL);
  const [image, setImage] = useState<string>(props.image);
  const [imageChanged, setImageChanged] = useState<boolean>(false);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [taking, setTaking] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState(templateCustomFields());
  const [name, setName] = useState<string>(props.asset.name);
  const [nameInvalid, setNameInvalid] = useState<boolean>(false);
  const [labelsInvalid, setLabelsInvalid] = useState<boolean>(false);
  const [manufacturer, setManufacturer] = useState<string>(props.asset.manufacturer);
  const [serial, setSerial] = useState<string>(props.asset.serial);
  const [description, setDescription] = useState<string>(props.asset.description);
  const [model, setModel] = useState<string>(props.asset.model);
  const [floorDropdownVisible, setFloorDropdownVisible] = useState<boolean>(false);
  const [areaDropdownVisible, setAreaDropdownVisible] = useState<boolean>(false);
  const [spaceDropdownVisible, setSpaceDropdownVisible] = useState<boolean>(false);
  const [buildingInvalid, setBuildingInvalid] = useState<boolean>(false);
  const [floorInvalid, setFloorInvalid] = useState<boolean>(false);
  const [areaInvalid, setAreaInvalid] = useState<boolean>(false);
  const [spaceInvalid, setSpaceInvalid] = useState<boolean>(false);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>();
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [customFieldsValid,
    setCustomFieldsValid] = useState<boolean[]>(customFields?.map(() => true) ?? []);

  const ref = useRef(null);
  const insets = useSafeAreaInsets();

  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>('');
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>(null);
  const [editSuccess, setEditSuccess] = useState<boolean>(false);
  const [areasCanHaveAssets, setAreasCanHaveAssets] = useState<boolean>(true);
  const [spacesCanHaveAssets, setSpacesCanHaveAssets] = useState<boolean>(true);
  const [taskComplete, setTaskComplete] = useState<boolean>(false);
  const [locationChanged, setLocationChanged] = useState<boolean>(false);
  const [labels, setLabels] = useState<(SensorLabel | MeterLabel)[]>(props.asset.labels
    ?.filter((label) => Object
      .values(props.templateType === TemplateType.SENSOR ? SensorLabel : MeterLabel)
      .includes(label)) ?? []);
  const [labelsDropdownShown, setLabelsDropdownShown] = useState<boolean>(false);
  const buildings: object = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canDeleteAssets: boolean = isAdmin
    || permissions?.includes(UserPermission.ASSETS_DELETE);
  const selectedTicketDetails: SelectedTicketState = useSelector((state:
    { selectedTicket: SelectedTicketState }) => state.selectedTicket);
  const selectedTicket: Ticket | undefined = selectedTicketDetails.ticket;
  const dispatch = useDispatch();

  useEffect(() => {
    let found: boolean = false;
    Object.keys(buildings ?? {}).forEach((buildingId: string) => {
      const building: Building = buildings[buildingId];
      if (found) return;
      if (buildingId === props.asset.locationId) {
        found = true;
        setSelectedBuilding(building);
        setSelectedFloor(null);
        setSelectedArea(null);
        setSelectedSpace(null);
      } else {
        Object.keys(building.floors ?? {}).forEach((floorId: string) => {
          const floor: Floor = building.floors[floorId];
          if (floorId === props.asset.locationId) {
            found = true;
            setSelectedBuilding(building);
            setSelectedFloor(floor);
            setSelectedArea(null);
            setSelectedSpace(null);
          } else {
            Object.keys(floor.areas ?? {}).forEach((areaId: string) => {
              const area: Area = floor.areas[areaId];
              if (areaId === props.asset.locationId) {
                found = true;
                setSelectedBuilding(building);
                setSelectedFloor(floor);
                setSelectedArea(area);
                setSelectedSpace(null);
              } else {
                Object.keys(area.spaces).forEach((spaceId: string) => {
                  const space: Space = area.spaces[spaceId];
                  if (spaceId === props.asset.locationId) {
                    found = true;
                    setSelectedBuilding(building);
                    setSelectedFloor(floor);
                    setSelectedArea(area);
                    setSelectedSpace(space);
                  }
                });
              }
            });
          }
        });
      }
    });
  }, [props.asset.locationId, props.visible]);

  useEffect(() => {
    setFloorInvalid(false);
    setFloorDropdownVisible(false);
  }, [selectedFloor]);

  useEffect(() => {
    setAreaInvalid(false);
    setAreaDropdownVisible(false);
  }, [selectedArea]);

  useEffect(() => {
    setSpaceInvalid(false);
    setSpaceDropdownVisible(false);
  }, [selectedSpace]);

  useEffect(() => {
    setFloorDropdownVisible(false);
    setAreaDropdownVisible(false);
    setSpaceDropdownVisible(false);
  }, [tabSelected]);

  const showAlert = (
    title: string,
    message?: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertVisible(true);
  };

  const closeDropdowns: () => void = () => {
    setFloorDropdownVisible(false);
    setAreaDropdownVisible(false);
    setSpaceDropdownVisible(false);
    setLabelsDropdownShown(false);
  };

  const restoreDefaults = () => {
    setName(props.asset.name);
    setNameInvalid(false);
    setLabelsInvalid(false);
    setModel(props.asset.model);
    setSerial(props.asset.serial);
    setDescription(props.asset.description);
    setManufacturer(props.asset.manufacturer);
    setSelectedArea(null);
    setSelectedFloor(null);
    setSelectedSpace(null);
    setBuildingInvalid(false);
    setFloorInvalid(false);
    setAreaInvalid(false);
    setCustomFields(templateCustomFields());
    setImage(props.image);
    setImageChanged(false);
    setTabSelected(AssetFormTab.ESSENTIAL);
    closeDropdowns();
    setTaskComplete(false);
    setLocationChanged(false);
  };

  const handleImage = (uri: string) => {
    Image.getSize(uri, (w, h) => {
      manipulateAsync(uri, [{ resize: { height: Math.min(h, 1080) } }], {
        base64: true,
        compress: 0.3,
      }).then((m) => {
        setImage(`data:image/jpeg;base64,${m.base64}`);
        setImageChanged(true);
      });
    });
  };

  const pickImage = async () => {
    logEvent('EditAssetModal', 'Upload');
    // No permissions request is necessary for launching the image library
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.2,
      base64: true,
    });

    if (result.assets[0]?.uri) handleImage(result.assets[0]?.uri);
  };

  const convertFields = (fields: AssetCustomField[]) => fields
    ?.map((field: AssetCustomField) => {
      if (field.value.trim()?.length > 0 && field.format === ADDITIONAL_FIELD_TYPES.DATE) {
        let date: Date = new Date(field.value.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$2/$1/$3'));
        if (isMobile) {
          const dateComponents: number[] = getDateComponents(field.value);
          date = new Date(dateComponents[2], dateComponents[1] - 1, dateComponents[0]);
        }
        if (date instanceof Date && !Number.isNaN(date.getTime())) {
          return ({ ...field, value: `${date?.toISOString()}` });
        }
        return field;
      }
      return field;
    });

  const deleteError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? 'Unable to delete the asset at the moment. Please try again later',
  );

  const attemptUpdateAsset: () => void = () => {
    const LocationId: string = selectedSpace?.LocationId ?? selectedArea?.LocationId
      ?? selectedFloor?.LocationId;
    const resetMapLocation: boolean = LocationId !== props.asset.locationId;
    const Name: string = name?.trim();
    const Manufacturer: string = manufacturer?.trim();
    const Description: string = description?.trim();
    const Model: string = model?.trim();
    const Serial: string = serial?.trim();
    const LocationBuilding: string = selectedBuilding?.Name?.trim();
    const LocationFloor: string = selectedFloor?.Name?.trim();
    const LocationArea: string = selectedArea?.Name?.trim();
    const LocationSpace: string = selectedSpace?.Name?.trim();
    let CustomFields: AssetCustomField[] = convertFields(customFields);
    if (resetMapLocation) {
      CustomFields = CustomFields.filter(
        (field) => field.name !== 'GeoX' && field.name !== 'GeoY',
      );
    }
    updateAsset({
      assetId: props.asset.id,
      model: Model,
      serial: Serial,
      manufacturer: Manufacturer,
      description: Description,
      locationId: LocationId,
      customFields: CustomFields,
      name: Name,
      labels,
    }).then((response) => {
      const success = response && !response.isError;
      if (success) {
        setEditSuccess(true);
        props.updateAsset({
          ...props.asset,
          name: Name,
          model: Model,
          serial: Serial,
          description: Description,
          manufacturer: Manufacturer,
          locationId: LocationId,
          locationBuilding: LocationBuilding,
          locationFloor: LocationFloor,
          locationArea: LocationArea,
          locationSpace: LocationSpace,
          customFields: CustomFields,
          imageUrl: image,
          labels,
        });
        setLocationChanged(resetMapLocation);
        setTaskComplete(true);
        if (resetMapLocation) {
          dispatch(updateSelectedTicket({
            ...selectedTicket,
            onMap: false,
            floorName: LocationFloor,
            locationName: (LocationSpace || LocationArea || ''),
            pinned: true,
          }));
        }
      } else showAlert('Error', UPDATE_ASSET_FAILURE);
      setLoading(false);
    }).catch(() => {
      showAlert('Error', UPDATE_ASSET_FAILURE);
      setLoading(false);
    });
  };

  const save: () => void = () => {
    if (loading) return;
    setFloorDropdownVisible(false);
    setAreaDropdownVisible(false);
    setLoading(true);
    logEvent('EditAssetModal', 'Save');
    if (imageChanged) {
      uploadAssetImage(props.asset.id, image).then((result) => {
        if (result && !result.isError) {
          props.updateImage(image);
          attemptUpdateAsset();
        } else {
          showAlert('Error', IMAGE_UPLOAD_FAILURE);
          setLoading(false);
        }
      });
    } else attemptUpdateAsset();
  };

  const deleteAsset: () => void = () => {
    showAlert(
      'Delete the asset?',
      <Text>
        {'Do you want to delete the asset - '}
        <Text style={Style.alertBoldText}>
          {props.asset.name}
        </Text>
        {'?'}
      </Text>,
      true,
      () => {
        setLoading(true);
        retireAsset(props.asset.id).then((response) => {
          if (response && !response?.isError) {
            props.showAlert?.(
              'Success',
              <Text>
                {'Asset '}
                <Text style={Style.alertBoldText}>{props.asset.name}</Text>
                {' deleted successfully'}
              </Text>,
              false,
            );
            props.deleteAsset?.(props.asset.id);
            props.setModalVisible(false);
          } else deleteError(response?.errorText);
        }).catch((error) => deleteError(error?.errorText)).finally(() => setLoading(false));
      },
      'Delete',
    );
  };

  useEffect(() => {
    if (!alertVisible && !alertError && editSuccess) props.setModalVisible(false);
  }, [alertVisible]);

  useEffect(() => setLabelsInvalid(false), [labels]);

  useEffect(closeDropdowns, [tabSelected]);

  useEffect(() => {
    setAreasCanHaveAssets(selectedBuilding?.Area?.IsAssetAvailable);
    setSpacesCanHaveAssets(selectedBuilding?.Space?.IsAssetAvailable);
  }, [selectedBuilding]);

  const syncFloors: () => void = () => {
    setLoading(true);
    GetEntireLocationStructure(
      (locations) => {
        dispatch(updateLocations(locations));
        setLoading(false);
        showAlert(
          'Success',
          'Floor data has been successfully synchronized.',
          false,
        );
      },
      () => {
      },
    );
  };

  const validate: () => void = () => {
    if (tabSelected === AssetFormTab.ESSENTIAL) {
      setTabSelected(AssetFormTab.LOCATION);
      return;
    }
    if (tabSelected === AssetFormTab.LOCATION) {
      setTabSelected(AssetFormTab.ADDITIONAL);
      return;
    }
    logEvent('EditAssetModal', 'Save');
    const errorMessages: string[] = [];
    setNameInvalid(!(name?.trim().length > 0));
    if (!(name?.trim().length > 0)) {
      if (!(errorMessages.length)) setTabSelected(AssetFormTab.ESSENTIAL);
      errorMessages.push('- Asset name cannot be empty');
      setNameInvalid(true);
    }
    if (props.templateType !== TemplateType.ASSET && !(labels?.length)) {
      if (!(errorMessages.length)) setTabSelected(AssetFormTab.ESSENTIAL);
      errorMessages.push('- At least one label must be selected');
      setLabelsInvalid(true);
    }
    if (!selectedBuilding) {
      if (!(errorMessages.length)) setTabSelected(AssetFormTab.LOCATION);
      errorMessages.push('- A building must be selected');
      setBuildingInvalid(true);
    }
    if (!selectedFloor) {
      if (!(errorMessages.length)) setTabSelected(AssetFormTab.LOCATION);
      errorMessages.push('- A floor must be selected');
      setFloorInvalid(true);
    }
    if (!areasCanHaveAssets && spacesCanHaveAssets && selectedArea && !selectedSpace) {
      if (!(errorMessages.length)) setTabSelected(AssetFormTab.LOCATION);
      errorMessages.push('- Areas cannot have assets in this building');
      setAreaInvalid(true);
    }
    if (!spacesCanHaveAssets && selectedSpace) {
      if (!(errorMessages.length)) setTabSelected(AssetFormTab.LOCATION);
      errorMessages.push('- Spaces cannot have assets in this building');
      setSpaceInvalid(true);
    }
    const updatedCustomFieldsValid: boolean[] = [...customFieldsValid];
    convertFields(customFields)?.forEach((field: AssetCustomField, index: number) => {
      if (field.value?.length > 0
        && !new RegExp(field.format ?? ADDITIONAL_FIELD_TYPES.STANDARD).test(field.value)) {
        updatedCustomFieldsValid[index] = false;
        if (!(errorMessages.length)) setTabSelected(AssetFormTab.ADDITIONAL);
        errorMessages.push(`- Invalid value for ${field.name}: ${field.value}`);
      } else updatedCustomFieldsValid[index] = true;
    });
    setCustomFieldsValid(updatedCustomFieldsValid);
    if (!(errorMessages.length)) save();
    else showAlert('Error', errorMessages.join('\n'));
  };

  const renderSaveContinueButton = () => (
    <PressableStyled
      style={styles.saveButton}
      disabled={loading}
      testID='save-asset-changes-btn'
      onPress={validate}
    >
      <Text style={styles.saveButtonText}>
        {tabSelected === AssetFormTab.ADDITIONAL ? 'Save' : 'Continue'}
      </Text>
    </PressableStyled>
  );

  const renderBackCancelButton = () => <PressableStyled
    style={styles.cancelButton}
    onPress={() => {
      logEvent('EditAssetModal', 'Cancel');
      props.setModalVisible(false);
    }}
  >
    <Text style={styles.cancelButtonText}>
      {'Cancel'}
    </Text>
  </PressableStyled>;

  const renderCustomField = (field: AssetCustomField, index: number) => {
    const title: string = field.name.trim();
    const fieldType: AdditionalTypeDetails = ADDITIONAL_FIELD_TYPE_DETAILS
      .filter((type: AdditionalTypeDetails) => type.Format === field.format)[0];
    let testID: string = 'text-add-field';
    if (field.format === ADDITIONAL_FIELD_TYPES.DATE) testID = 'date-add-field';
    else if (field.format === ADDITIONAL_FIELD_TYPES.IP) testID = 'ip-add-field';
    else if (field.format === ADDITIONAL_FIELD_TYPES.MAC) testID = 'mac-add-field';
    else if (field.format === ADDITIONAL_FIELD_TYPES.NUMERIC) testID = 'number-add-field';
    else if (field.format === ADDITIONAL_FIELD_TYPES.PORT) testID = 'port-add-field';
    else if (field.format === ADDITIONAL_FIELD_TYPES.LINK) testID = 'link-add-field';
    return <View
      style={styles.additionalField}
      {...{ testID }}
    >
      <Text style={styles.additionalFieldTitle}>{title}</Text>
      <Text style={styles.additionalFieldType}>
        {fieldType?.description ?? ADDITIONAL_FIELD_TYPE_DETAILS[0].description}
      </Text>
      <CustomTextField
        placeholder={fieldType?.placeholder ?? ADDITIONAL_FIELD_TYPE_DETAILS[0].placeholder}
        value={field.value}
        invalid={!customFieldsValid[index]}
        errorText={fieldType?.invalidMessage ?? ADDITIONAL_FIELD_TYPE_DETAILS[0].invalidMessage}
        updateValue={(text: string) => {
          const isValid: boolean = (fieldType?.Format === ADDITIONAL_FIELD_TYPES.STANDARD
            || fieldType?.Format === ADDITIONAL_FIELD_TYPES.NUMERIC || !fieldType?.Format)
            ? (text.trim().length === 0 || new RegExp(field.format).test(text.trim())) : true;
          setCustomFieldsValid(customFieldsValid.map((value: boolean, valueIndex: number) => (
            valueIndex === index ? isValid : value)));
          setCustomFields(customFields?.filter((fieldFilter:
            AssetCustomField) => !(fieldFilter?.name?.toLowerCase().includes('geo')))
            .map((customField: AssetCustomField, fieldIndex: number) => (fieldIndex
              === index ? { ...customField, value: text } : customField)));
        }}
        scanner={field.name.toLowerCase().includes('mac')}
      />
    </View>;
  };

  const wrapContent = (content: ReactElement) => (isMobile ? content : <Pressable
    children={content}
    onPress={closeDropdowns}
  />);

  const closeButton = () => <PressableStyled
    onPress={() => {
      logEvent('EditAssetModal', 'Close_Modal');
      props.setModalVisible(false);
    }}
    style={[
      styles.closeButton,
      isMobile && { top: Platform.OS === 'ios' ? Math.max(insets.top, 10) : 16 },
    ]}
    testID='close-changes-saved-modal-btn'
  >
    <DeleteIcon24 color={Colors.light.hereworksBlack600} />
  </PressableStyled>;

  const content = () => (taskComplete
    ? <View
      style={isMobile ? styles.containerSuccessMobile : styles.containerSuccessWeb}
      testID='changes-saved-modal'
    >
      <View style={styles.iconContainer}>
        {isMobile ? <ResolvedIconMobile /> : <ResolvedIcon />}
      </View>
      <Text style={styles.successTitle}>{'Changes saved'}</Text>
      <Text style={styles.successMessage}>
        {'Asset '}
        <Text style={Style.alertBoldText}>{name}</Text>
        {' has been updated'}
      </Text>
      {locationChanged && <View
        style={[Style.row, styles.locationChangeBox]}
        testID='location-successfully-changed-popup'
      >
        <View style={styles.locationChangeIcon}>
          <AlertIcon12 />
        </View>
        <Text style={styles.locationChangeText}>
          {'You have just changed the location of this asset.'}
          {' Its position has been removed from the map and moved to the new location\'s storage.'}
          {' It can be assigned a new position on the map in the new location.'}
        </Text>
      </View>}
      {closeButton()}
    </View> : <View
      style={[
        Style.modalContent, styles.containerStyle,
        { paddingBottom: Math.max(insets.bottom, 40) },
        !isMobile ? styles.containerStyleWeb
          : { paddingTop: Platform.OS === 'ios' ? Math.max(insets.top, 10) : 10 },
      ]}
    >
      <Text style={styles.title}>{'Edit asset'}</Text>
      <View style={styles.tabButtonContainer}>
        {[AssetFormTab.ESSENTIAL, AssetFormTab.LOCATION, AssetFormTab.ADDITIONAL]
          .map((tab: AssetFormTab) => {
            const selected: boolean = tabSelected === tab;
            return <PressableStyled
              key={`tab_option_${tab}`}
              style={[
                styles.smallButton, styles.tabButton,
                {
                  backgroundColor: selected ? Colors.light.hereworksBlue500 : '#FFF',
                  borderColor: selected
                    ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlue300,
                },
              ]}
              onPress={() => setTabSelected(tab)}
              testID={`am-edit-${tab.toLowerCase()}-fields-btn`}
            >
              <Text
                style={[
                  styles.smallButtonText,
                  { color: selected ? '#FFF' : Colors.light.hereworksBlue400 },
                ]}
              >
                {tab}
              </Text>
            </PressableStyled>;
          })}
      </View>
      <KeyboardAvoidingView
        style={{ width: '100%', flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          nestedScrollEnabled
          style={[styles.tabContent, styles.tabContent]}
          contentContainerStyle={styles.tabContentContainer}
        >
          {tabSelected === AssetFormTab.ESSENTIAL && (
            <>
              <Text style={styles.subTitle}>{'Fields marked with * are mandatory'}</Text>
              <View style={styles.imageContainer}>
                {image?.length > 0 ? (
                  <Image
                    key={Date.now()}
                    source={{ uri: image }}
                    resizeMode={'cover'}
                    style={styles.image}
                  />
                ) : (
                  <View
                    style={{
                      width: 120,
                      height: 120,
                      backgroundColor: Colors.light.hereworksWhite200,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlaceholderImageIcon />
                  </View>
                )}
                <View style={{ marginLeft: 24, justifyContent: 'center' }}>
                  <Text style={styles.imageTitle}>{'Image'}</Text>
                  <View style={{ marginTop: 16, flexDirection: isMobile ? 'column' : 'row' }}>
                    {isMobile && (
                      <PressableStyled
                        style={[
                          styles.smallButton,
                          {
                            alignItems: 'center',
                            alignSelf: isMobile ? 'stretch' : 'auto',
                          },
                        ]}
                        onPress={() => setCameraOpen(true)}
                      >
                        <Text style={styles.smallButtonText}>{'Take a photo'}</Text>
                      </PressableStyled>
                    )}
                    <PressableStyled
                      style={[
                        styles.smallButton,
                        {
                          backgroundColor: '#fff',
                          borderColor: Colors.light.hereworksBlue400,
                          marginTop: isMobile ? 8 : 0,
                          alignItems: 'center',
                          alignSelf: isMobile ? 'stretch' : 'auto',
                        },
                      ]}
                      onPress={pickImage}
                    >
                      <Text style={[
                        styles.smallButtonText, { color: Colors.light.hereworksBlue500 },
                      ]}>
                        {'Upload'}
                      </Text>
                    </PressableStyled>
                    {image?.length > 0 && <PressableStyled
                      style={[
                        styles.smallButton,
                        {
                          backgroundColor: '#fff',
                          borderColor: Colors.light.hereworksRed500,
                          marginTop: isMobile ? 8 : 0,
                          alignItems: 'center',
                          alignSelf: isMobile ? 'stretch' : 'auto',
                        },
                      ]}
                      onPress={() => {
                        setImage('');
                        setImageChanged(true);
                      }}
                    >
                      <Text style={[
                        styles.smallButtonText, { color: Colors.light.hereworksRed500 },
                      ]}>
                        {'Remove image'}
                      </Text>
                    </PressableStyled>}
                  </View>
                </View>
              </View>
              <CustomTextField
                title={'Name'}
                required
                invalid={nameInvalid}
                value={name}
                updateValue={(text: string) => {
                  setNameInvalid(!(text.toString().trim().length > 0));
                  setName(text);
                }}
                testID='am-asset-name-input-field'
              />
              <AssetLabelSelector
                {...{
                  labels, setLabels, labelsDropdownShown, setLabelsDropdownShown,
                }}
                templateType={props.templateType}
                invalid={labelsInvalid}
              />
              <CustomTextField
                title={'Description'}
                value={description}
                updateValue={setDescription}
                testID='am-asset-description-input-field'
              />
              <CustomTextField
                title={'Manufacturer'}
                value={manufacturer}
                updateValue={setManufacturer}
                testID='am-asset-manufacturer-input-field'
              />
              <CustomTextField
                title={'Model'}
                value={model}
                updateValue={setModel}
                testID='am-asset-model-input-field'
              />
              <CustomTextField
                title={'Serial number'}
                value={serial}
                updateValue={setSerial}
                scanner
                testID='am-asset-serialno-input-field'
              />
              <CustomTextField
                title={'Template'}
                value={props.asset.templateName}
                editable={false}
                updateValue={null}
              />
              <CustomTextField
                title={'Asset ID'}
                value={props.asset.id}
                editable={false}
                updateValue={null}
              />
            </>
          )}
          {tabSelected === AssetFormTab.LOCATION && (
            <Pressable onPress={closeDropdowns}>
              <Text style={styles.subTitle}>{'Fields marked with * are mandatory'}</Text>
              <View>
                <Text style={styles.heading}>{'Building *'}</Text>
                <View style={[
                  styles.dropdownBox, buildingInvalid && styles.dropdownBoxInvalid,
                ]}>
                  <Text style={styles.dropdownBoxText}>{selectedBuilding?.Name}</Text>
                </View>
                {selectedBuilding && <>
                  <View style={Style.row}>
                    <Text style={styles.heading}>{'Floor *'}</Text>
                    <Pressable
                      onPress={syncFloors}
                      style={(state: { hovered: boolean, pressed: boolean }) => [
                        { marginTop: styles.heading.marginTop },
                        state.hovered && Style.hovered, state.pressed && Style.pressed,
                      ]}
                      testID='sync-floors-button'
                    >
                      <Text style={styles.syncButtonText}>{loading ? 'Syncing...' : 'Sync Floors'}</Text>
                    </Pressable>
                  </View>
                  <PressableStyled
                    style={[styles.dropdownBox, floorInvalid && styles.dropdownBoxInvalid]}
                    onPress={() => {
                      setFloorDropdownVisible(!floorDropdownVisible);
                      setAreaDropdownVisible(false);
                      setSpaceDropdownVisible(false);
                    }}
                    testID='select-floor-button'
                  >
                    <Text style={styles.dropdownBoxText}>{selectedFloor?.Name ?? 'Select a floor'}</Text>
                    <View style={{ alignItems: 'flex-end' }}>
                      <FontAwesome
                        size={20}
                        style={{
                          color: floorInvalid ? Colors.light.hereworksRed500
                            : Colors.light.hereworksLightBlue,
                          marginRight: 12,
                        }}
                        name={floorDropdownVisible ? 'angle-up' : 'angle-down'}
                      />
                    </View>
                  </PressableStyled>
                  {floorInvalid && <Text style={styles.errorText}>{'Please select a floor'}</Text>}
                  {floorDropdownVisible && <RegionDropdown
                    regions={Object.values(selectedBuilding?.floors ?? {})
                      ?.sort((prev: Floor, next: Floor) => prev.Index - next.Index)}
                    selectedRegion={selectedFloor}
                    setSelectedRegion={(floor: Floor) => {
                      setSelectedFloor(floor);
                      setSelectedArea(null);
                      setSelectedSpace(null);
                    }}
                    testID='edit-asset-floor-list'
                  />}
                </>}
                {(areasCanHaveAssets || spacesCanHaveAssets)
                  && Object.values(selectedFloor?.areas ?? {}).length > 0
                  && <View style={{ marginStart: 16 }}>
                    <View style={{ flexDirection: 'row' }}>
                      <View style={styles.connectorContainer}>
                        <View style={styles.floorAreaConnector} />
                        <View style={styles.connectorSpot} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <View style={Style.row}>
                          <Text style={styles.heading}>{'Area'}</Text>
                          {selectedArea && <PressableStyled
                            onPress={() => {
                              setSelectedArea(null);
                              setSelectedSpace(null);
                            }}
                            style={{ marginTop: styles.heading.marginTop }}
                          >
                            <DeleteIcon24 />
                          </PressableStyled>}
                        </View>
                        <PressableStyled
                          style={[styles.dropdownBox, areaInvalid && styles.dropdownBoxInvalid]}
                          onPress={() => {
                            setAreaDropdownVisible(!areaDropdownVisible);
                            setFloorDropdownVisible(false);
                            setSpaceDropdownVisible(false);
                          }}
                          testID='select-area-button'
                        >
                          <Text style={styles.dropdownBoxText}>{selectedArea?.Name ?? 'Select an area'}</Text>
                          <View style={{ alignItems: 'flex-end' }}>
                            <FontAwesome
                              size={20}
                              style={{
                                color: areaInvalid ? Colors.light.hereworksRed500
                                  : Colors.light.hereworksLightBlue,
                                marginRight: 12,
                              }}
                              name={areaDropdownVisible ? 'angle-up' : 'angle-down'}
                            />
                          </View>
                        </PressableStyled>
                      </View>
                    </View>
                    <View style={{ marginStart: 20 }}>
                      {areaInvalid && <Text style={styles.errorText}>
                        {'Please select an area'}
                      </Text>}
                      {areaDropdownVisible && <RegionDropdown
                        regions={Object.values(selectedFloor?.areas ?? {})
                          ?.sort((prev: Area, next: Area) => prev.Name.localeCompare(next.Name))}
                        selectedRegion={selectedArea}
                        setSelectedRegion={(area: Area) => {
                          setSelectedArea(area);
                          setSelectedSpace(null);
                        }}
                        testID='edit-asset-area-list'
                      />}
                    </View>
                  </View>}
                {spacesCanHaveAssets && Object.values(selectedArea?.spaces ?? {}).length > 0
                  && <View style={{ marginStart: 44 }}>
                    <View style={{ flexDirection: 'row' }}>
                      <View style={styles.connectorContainer}>
                        <View style={styles.floorAreaConnector} />
                        <View style={styles.connectorSpot} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <View style={Style.row}>
                          <Text style={styles.heading}>{'Space'}</Text>
                          {selectedSpace && <PressableStyled
                            onPress={() => setSelectedSpace(null)}
                            style={{ marginTop: styles.heading.marginTop }}
                          >
                            <DeleteIcon24 />
                          </PressableStyled>}
                        </View>
                        <PressableStyled
                          style={[styles.dropdownBox, areaInvalid && styles.dropdownBoxInvalid]}
                          onPress={() => {
                            setSpaceDropdownVisible(!spaceDropdownVisible);
                            setFloorDropdownVisible(false);
                            setAreaDropdownVisible(false);
                          }}
                          testID='select-space-button'
                        >
                          <Text style={styles.dropdownBoxText}>
                            {selectedSpace?.Name ?? 'Select a space'}
                          </Text>
                          <View style={{ alignItems: 'flex-end' }}>
                            <FontAwesome
                              size={20}
                              style={{
                                color: spaceInvalid ? Colors.light.hereworksRed500
                                  : Colors.light.hereworksLightBlue,
                                marginRight: 12,
                              }}
                              name={spaceDropdownVisible ? 'angle-up' : 'angle-down'}
                            />
                          </View>
                        </PressableStyled>
                      </View>
                    </View>
                    <View style={{ marginStart: 20 }}>
                      {spaceInvalid
                        && <Text style={styles.errorText}>{'Please select a space'}</Text>}
                      {spaceDropdownVisible && <RegionDropdown
                        regions={Object.values(selectedArea?.spaces ?? {})
                          .sort((prev: Space, next: Space) => prev.Name.localeCompare(next.Name))}
                        selectedRegion={selectedSpace}
                        setSelectedRegion={setSelectedSpace}
                        testID='edit-asset-space-list'
                      />}
                    </View>
                  </View>}
              </View>
            </Pressable>
          )}
          {tabSelected === AssetFormTab.ADDITIONAL && <>
            {customFields?.filter((field: AssetCustomField) => !(field?.name?.toLowerCase().includes('geo')))
              .map((field, index) => <View key={index}>
                {renderCustomField(field, index)}
              </View>)}
            {!(customFields?.filter((field: AssetCustomField) => !(field?.name?.toLowerCase().includes('geo')))
              .length > 0) && <View style={styles.noFieldsTextContainer}>
                <Text style={styles.noFieldsText}>{'No additional fields'}</Text>
              </View>}
          </>}
        </ScrollView>
        <View style={styles.buttonContainer}>
          {isMobile && canDeleteAssets && <View style={{ flex: 1 }}>
            <PressableStyled
              style={styles.deleteButton}
              onPress={deleteAsset}
              testID='am-delete-asset-btn'
            >
              <BinIcon12 color={Colors.light.hereworksRed700} />
            </PressableStyled>
          </View>}
          {!isMobile && renderBackCancelButton()}
          {renderSaveContinueButton()}
          {!isMobile && canDeleteAssets && (
            <View style={{ flex: 1 }}>
              <PressableStyled
                style={styles.deleteButton}
                onPress={deleteAsset}
                testID='am-delete-asset-btn'
              >
                <BinIcon24 color={Colors.light.hereworksRed} />
              </PressableStyled>
            </View>
          )}
        </View>
      </KeyboardAvoidingView>
      {isMobile && <PressableStyled
        onPress={() => {
          if (tabSelected === AssetFormTab.ADDITIONAL) {
            setTabSelected(AssetFormTab.LOCATION);
            return;
          }
          if (tabSelected === AssetFormTab.LOCATION) {
            setTabSelected(AssetFormTab.ESSENTIAL);
            return;
          }
          logEvent('EditAssetModal', 'Close_Modal');
          props.setModalVisible(false);
        }}
        style={[
          styles.backButton,
          isMobile && { top: Platform.OS === 'ios' ? Math.max(insets.top, 10) : 16 },
        ]}
      >
        <FontAwesome
          size={24}
          name='angle-left'
          color={Colors.light.hereworksBlack600}
        />
      </PressableStyled>}
      {closeButton()}
      {cameraOpen
        ? <CameraView
          style={[styles.camera, StyleSheet.absoluteFill]}
          {...{ ref }}
        >
          <PressableStyled
            style={styles.captureButton}
            disabled={taking}
            onPress={() => {
              // Haptics.notificationAsync();
              // Haptics.selectionAsync();
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
              setTaking(true);
              ref.current
                .takePictureAsync({ base64: true, quality: 0.1 })
                .then((picture) => {
                  handleImage(`data:image/jpeg;base64,${picture.base64}`);
                  setCameraOpen(false);
                  setTaking(false);
                });
            }}
          >
            <View
              style={{
                backgroundColor: '#fff',
                width: 50,
                height: 50,
                borderRadius: 25,
              }}
            />
            {taking && <ActivityIndicator style={{ position: 'absolute' }} />}
          </PressableStyled>
          <PressableStyled
            onPress={() => {
              logEvent('EditAssetModal', 'Close_Camera');
              setCameraOpen(false);
            }}
            style={[Style.closeButton, isMobile && { top: 40 }]}
            testID='am-close-edit-asset-modal-btn'
          >
            <CloseModalIcon />
          </PressableStyled>
        </CameraView> : null}
    </View>);

  return <Modal
    isVisible={props.visible}
    animationIn={isMobile ? 'slideInUp' : 'fadeIn'}
    animationOut={isMobile ? 'slideOutDown' : 'fadeOut'}
    onBackButtonPress={() => props.setModalVisible(false)}
    onBackdropPress={() => props.setModalVisible(false)}
    style={Style.modal}
    onModalHide={restoreDefaults}
  >
    {wrapContent(content())}
    <AlertModal
      action={alertAction}
      actionTitle={alertActionTitle}
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      setModalVisible={setAlertVisible}
    />
    {loading && <View style={styles.loadingIndicator}>
      <ActivityIndicator
        animating={loading}
        size='large'
        style={Style.activityIndicator}
      />
    </View>}
  </Modal>;
}

const styles = StyleSheet.create({
  containerStyle: {
    width: '100%',
    paddingHorizontal: 12,
    alignItems: 'center',
    alignSelf: 'center',
    height: '100%',
    borderRadius: 0,
  },
  containerSuccessWeb: {
    alignSelf: 'center',
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: 40,
    borderWidth: 1,
    borderColor: Colors.light.hereworksGreen400,
    width: 590,
    maxWidth: '95%',
    justifyContent: 'center',
    alignItems: 'center',
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
    shadowOpacity: 0.05,
    shadowColor: '#000',
  },
  containerSuccessMobile: {
    padding: 16,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    paddingTop: '40%',
    backgroundColor: '#fff',
  },
  iconContainer: {
    width: isMobile ? 40 : 64,
    height: isMobile ? 40 : 64,
    borderRadius: 32,
    backgroundColor: Colors.light.hereworksGreen400,
    justifyContent: 'center',
    alignItems: 'center',
  },
  successTitle: {
    fontSize: isMobile ? 20 : 28,
    lineHeight: isMobile ? 24 : 34,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksGreen400,
    marginTop: isMobile ? 24 : 32,
    textAlign: 'center',
    width: '100%',
  },
  successMessage: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 22,
    color: Colors.light.hereworksBlack400,
    marginTop: isMobile ? 12 : 27,
    textAlign: 'center',
    width: '100%',
  },
  locationChangeBox: {
    marginTop: 24,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: Colors.light.hereworksWhite100,
    padding: 16,
  },
  locationChangeIcon: {
    marginEnd: 16,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Colors.light.hereworksBlue500,
    justifyContent: 'center',
    alignItems: 'center',
  },
  locationChangeText: {
    flex: 1,
    fontSize: 16,
    lineHeight: 22,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
  },
  containerStyleWeb: {
    maxHeight: 780,
    marginVertical: 40,
    width: 530,
    borderRadius: 10,
  },
  title: {
    marginTop: isMobile ? 6 : 60,
    fontSize: isMobile ? 14 : 22,
    lineHeight: isMobile ? 17.5 : 33,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    letterSpacing: 0,
    textAlign: 'center',
  },
  subTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  imageTitle: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
  },
  smallButtonText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    color: '#fff',
  },
  smallButton: {
    borderRadius: 4,
    marginRight: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    backgroundColor: Colors.light.hereworksBlue500,
    alignSelf: 'center',
    height: 32,
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  camera: {
    position: 'absolute',
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  captureButton: {
    borderWidth: 6,
    borderColor: '#fff',
    borderRadius: 35,
    width: 70,
    height: 70,
    backgroundColor: 'transparent',
    padding: 8,
    marginBottom: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteButton: {
    borderColor: Colors.light.hereworksRed,
    borderRadius: 4,
    borderWidth: 1,
    height: 40,
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    alignSelf: isMobile ? 'flex-start' : 'flex-end',
  },
  heading: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: 12,
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  dropdownView: {
    zIndex: 5,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    backgroundColor: '#fff',
    alignSelf: 'stretch',
    marginTop: 4,
    maxHeight: 180,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    end: 20,
  },
  backButton: {
    position: 'absolute',
    top: 16,
    start: 20,
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
  dropdownBox: {
    width: '100%',
    height: 44,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
  dropdownBoxInvalid: {
    borderColor: Colors.light.hereworksRed500,
  },
  errorText: {
    color: Colors.light.hereworksRed500,
    fontFamily: 'Roboto_400Regular',
    marginTop: 4,
    fontSize: 12,
    lineHeight: 20,
    alignSelf: 'flex-start',
  },
  textSelected: { color: '#FFF' },
  itemSelected: { backgroundColor: Colors.light.hereworksDarkBlue },
  tabContentContainer: {
    paddingBottom: 16,
  },
  tabContent: {
    flex: 1,
    alignSelf: 'stretch',
    paddingTop: 6,
    paddingHorizontal: isMobile ? 16 : 28,
    marginHorizontal: isMobile ? -8 : undefined,
  },
  buttonContainer: {
    flexDirection: 'row',
    marginTop: 27,
    alignSelf: 'stretch',
    marginHorizontal: isMobile ? 8 : 28,
  },
  tabButtonContainer: {
    flexDirection: 'row',
    marginTop: 32,
    marginBottom: 12,
    alignSelf: 'center',
  },
  tabButton: {
    marginStart: 4,
    marginEnd: 4,
    paddingHorizontal: 12,
    justifyContent: 'center',
    height: 28,
  },
  connectorContainer: {
    flexDirection: 'row',
    marginBottom: 17,
    alignSelf: 'stretch',
  },
  floorAreaConnector: {
    width: 16,
    marginBottom: 2,
    alignSelf: 'stretch',
    borderStartWidth: 1,
    borderBottomWidth: 1,
    borderBottomStartRadius: 6,
    borderColor: Colors.light.hereworksBlue100,
  },
  connectorSpot: {
    width: 3,
    height: 6,
    borderTopStartRadius: 3,
    borderBottomStartRadius: 3,
    backgroundColor: Colors.light.hereworksBlue100,
    alignSelf: 'flex-end',
  },
  additionalField: {
    marginBottom: 16,
  },
  additionalFieldTitle: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
  },
  additionalFieldType: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    marginBottom: 8,
  },
  saveButtonText: {
    color: '#fff',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
    textAlign: 'center',
  },
  cancelButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    marginRight: 12,
    flex: isMobile ? 1 : undefined,
  },
  cancelButtonText: {
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
    textAlign: 'center',
  },
  noFieldsTextContainer: {
    padding: 16,
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 10,
    marginBottom: 16,
    alignSelf: 'stretch',
  },
  noFieldsText: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  loadingIndicator: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: '#00000040',
  },
  saveButton: {
    backgroundColor: Colors.light.hereworksLightBlue,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    flex: isMobile ? 1 : undefined,
  },
  imageContainer: {
    marginTop: 12,
    flexDirection: 'row',
    borderWidth: 1,
    padding: 12,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    alignSelf: 'stretch',
  },
  image: {
    aspectRatio: 1,
    width: 120,
    height: 120,
  },
  syncButton: {
    backgroundColor: Colors.light.hereworksBlue100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginLeft: 8,
    marginTop: 4,
  },
  syncButtonText: {
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
  },
});
